import React from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const ACCENT = "#1473e6";

const fadeSlide = keyframes`
  0%, 8% { opacity: 0; transform: translateY(10px); }
  22%, 78% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-6px); }
`;

const shuffle = keyframes`
  0%, 12% { transform: translateX(0); }
  35% { transform: translateX(28px); }
  58% { transform: translateX(-14px); }
  80%, 100% { transform: translateX(0); }
`;

const drawLine = keyframes`
  0%, 10% { stroke-dashoffset: 120; opacity: 0.3; }
  40%, 75% { stroke-dashoffset: 0; opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0.85; }
`;

const pulseBtn = keyframes`
  0%, 20% { transform: scale(0.94); opacity: 0.55; }
  45%, 70% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.96); opacity: 0.7; }
`;

const stackIn = keyframes`
  0% { opacity: 0; transform: translateY(14px) scale(0.96); }
  30%, 100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 104,
        overflow: "hidden",
        bgcolor: tint,
      }}
    >
      {children}
    </Box>
  );
}

function RefsScene({ isDark }) {
  const cards = [
    { n: 1, x: 22, delay: "0s", color: "#8b5a2b" },
    { n: 2, x: 78, delay: "0.15s", color: "#3d5a80" },
    { n: 3, x: 134, delay: "0.3s", color: "#4a5568" },
  ];
  return (
    <Stage tint={isDark ? "#0e0e0e" : "#eef2f6"}>
      {cards.map((c) => (
        <Box
          key={c.n}
          sx={{
            position: "absolute",
            left: c.x,
            top: 22,
            width: 46,
            height: 60,
            borderRadius: 1.25,
            bgcolor: c.color,
            border: "1px solid",
            borderColor: alpha("#fff", isDark ? 0.2 : 0.35),
            animation: `${shuffle} 4.6s ease-in-out infinite`,
            animationDelay: c.delay,
            ...motionSafe,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 4,
              left: 4,
              width: 14,
              height: 14,
              borderRadius: 999,
              bgcolor: ACCENT,
              color: "#fff",
              fontSize: "0.55rem",
              fontWeight: 800,
              display: "grid",
              placeItems: "center",
            }}
          >
            {c.n}
          </Box>
        </Box>
      ))}
      <Box
        sx={{
          position: "absolute",
          bottom: 10,
          left: "50%",
          ml: "-40px",
          width: 80,
          height: 3,
          borderRadius: 99,
          bgcolor: alpha(ACCENT, 0.55),
          animation: `${fadeSlide} 4.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function PromptScene({ promptLabel, isDark }) {
  return (
    <Stage tint={isDark ? "#0e0e0e" : "#eef2f6"}>
      <Box
        sx={{
          position: "absolute",
          left: 16,
          top: 16,
          right: 16,
          height: 36,
          borderRadius: 1.25,
          bgcolor: isDark ? "#1a1a1a" : "#fff",
          border: "1px solid",
          borderColor: alpha("#000", isDark ? 0.25 : 0.1),
          px: 1.25,
          display: "flex",
          alignItems: "center",
          color: isDark ? "#7d7d7d" : "#666",
          fontSize: "0.7rem",
          animation: `${fadeSlide} 5s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {promptLabel}
      </Box>
      <Box
        component="svg"
        viewBox="0 0 48 40"
        sx={{
          position: "absolute",
          left: "50%",
          bottom: 10,
          ml: "-20px",
          width: 40,
          height: 34,
          "& path": {
            animation: `${drawLine} 4.8s ease-in-out infinite`,
            ...motionSafe,
          },
        }}
      >
        <path
          d="M10 14 L16 8 H20 L23 12 L26 8 H30 L36 14 L40 20 L34 22 V34 H14 V22 L8 20 Z"
          fill="none"
          stroke={isDark ? "#e8e8e8" : "#1e293b"}
          strokeWidth="1.4"
          strokeDasharray="120"
        />
      </Box>
    </Stage>
  );
}

function GenerateScene({ generateLabel, isDark }) {
  const chips = [
    { left: 14, top: 16, w: 42, color: "#1473e6" },
    { left: 62, top: 16, w: 42, color: "#0f766e" },
    { left: 14, top: 48, w: 42, color: "#4338ca" },
    { left: 62, top: 48, w: 42, color: "#a16207" },
  ];
  return (
    <Stage tint={isDark ? "#0e0e0e" : "#eef2f6"}>
      {chips.map((c, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: c.left,
            top: c.top,
            width: c.w,
            height: 22,
            borderRadius: 1,
            bgcolor: alpha(c.color, isDark ? 0.35 : 0.18),
            border: "1px solid",
            borderColor: alpha(c.color, 0.55),
            animation: `${stackIn} 4.6s ease-out infinite`,
            animationDelay: `${i * 0.12}s`,
            ...motionSafe,
          }}
        />
      ))}
      <Box
        sx={{
          position: "absolute",
          right: 16,
          top: 34,
          px: 1.75,
          py: 0.85,
          borderRadius: 999,
          bgcolor: ACCENT,
          color: "#fff",
          fontSize: "0.72rem",
          fontWeight: 800,
          animation: `${pulseBtn} 4.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {generateLabel}
      </Box>
    </Stage>
  );
}

function ResultScene({ svgLabel, isDark }) {
  return (
    <Stage tint={isDark ? "#0e0e0e" : "#eef2f6"}>
      <Box
        component="svg"
        viewBox="0 0 48 56"
        sx={{
          position: "absolute",
          left: 28,
          top: 18,
          width: 44,
          height: 56,
          "& path": {
            animation: `${drawLine} 4.8s ease-in-out infinite`,
            ...motionSafe,
          },
        }}
      >
        <path
          d="M10 14 L16 8 H20 L23 12 L26 8 H30 L36 14 L40 20 L34 22 V34 H14 V22 L8 20 Z"
          fill="none"
          stroke={isDark ? "#e8e8e8" : "#0f766e"}
          strokeWidth="1.4"
          strokeDasharray="120"
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 18,
          top: 36,
          px: 1.5,
          py: 0.75,
          borderRadius: 999,
          bgcolor: "#ea580c",
          color: "#fff",
          fontSize: "0.68rem",
          fontWeight: 800,
          animation: `${pulseBtn} 4.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {svgLabel}
      </Box>
    </Stage>
  );
}

function SceneCard({ preview, title, body, isDark }) {
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: isDark ? "rgba(255,255,255,0.1)" : "divider",
        bgcolor: "background.paper",
        boxShadow: isDark
          ? `0 12px 28px ${alpha("#000", 0.35)}`
          : `0 10px 28px ${alpha(ACCENT, 0.08)}`,
      }}
    >
      {preview}
      <Box sx={{ p: 1.4 }}>
        <Typography
          fontWeight={800}
          sx={{ letterSpacing: "-0.02em", fontSize: "0.95rem" }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, lineHeight: 1.45 }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

export default function NuevoDisenoHelpDialog({ open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "background.paper",
          backgroundImage: "none",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon
            sx={{ color: isDark ? "#8cb4ff" : ACCENT, fontSize: 22 }}
          />
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ letterSpacing: "-0.02em" }}
          >
            {t("presentacionesSearch.newDesign.help.title")}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, lineHeight: 1.45 }}
        >
          {t("presentacionesSearch.newDesign.help.lead")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 1,
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <SceneCard
            isDark={isDark}
            preview={<RefsScene isDark={isDark} />}
            title={t("presentacionesSearch.newDesign.help.refsTitle")}
            body={t("presentacionesSearch.newDesign.help.refsBody")}
          />
          <SceneCard
            isDark={isDark}
            preview={
              <PromptScene
                isDark={isDark}
                promptLabel={t("presentacionesSearch.newDesign.promptLabel")}
              />
            }
            title={t("presentacionesSearch.newDesign.help.promptTitle")}
            body={t("presentacionesSearch.newDesign.help.promptBody")}
          />
          <SceneCard
            isDark={isDark}
            preview={
              <GenerateScene
                isDark={isDark}
                generateLabel={t(
                  "presentacionesSearch.newDesign.generateMenu.open",
                  { defaultValue: "Generar" },
                )}
              />
            }
            title={t("presentacionesSearch.newDesign.help.generateTitle")}
            body={t("presentacionesSearch.newDesign.help.generateBody")}
          />
          <SceneCard
            isDark={isDark}
            preview={
              <ResultScene
                isDark={isDark}
                svgLabel={t("presentacionesSearch.newDesign.generateSvg", {
                  defaultValue: "Generar SVG",
                })}
              />
            }
            title={t("presentacionesSearch.newDesign.help.resultTitle")}
            body={t("presentacionesSearch.newDesign.help.resultBody")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 999,
            bgcolor: ACCENT,
            color: "#fff",
            "&:hover": { bgcolor: "#0d66d0" },
          }}
        >
          {t("presentacionesSearch.newDesign.help.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
